import { Api } from 'laravel-request';

import { baseApi, apiTags } from '../baseApi';
import type {
    ApiResponse,
    Service,
    ServiceListParams,
    UpdateServicePaidParams,
    AssignServicesParams,
    UpdateServiceArchiveParams,
    CalculateSuccessFeeParams,
    GenerateServiceExcelParams,
    ServicesOnConfirmationParams,
} from '../types/api.types';
import type { LaravelQueryBuilder } from '../types/laravelRequest';

/**
 * `laravelRequestBaseQuery` (см. baseApi.ts) обрезает ответ бэкенда до поля `data`,
 * из-за чего `meta.id` — а именно там бэкенд отдаёт id только что созданной записи,
 * по той же конвенции, что и все остальные "store"-ручки в приложении — теряется.
 * Для create/update услуг это критично (по нему обновляется список), поэтому здесь
 * используем queryFn напрямую, в обход baseQuery, и отдаём наверх ответ целиком.
 */
const callServiceRequest = <T>(
    request: { call: (onSuccess: (response: ApiResponse<T>) => void, onError: (xhr: any, responseData: any) => void) => unknown }
): Promise<{ data: ApiResponse<T> } | { error: { status: number; data: any } }> =>
    new Promise((resolve) => {
        request.call(
            (response) => resolve({ data: response }),
            (xhr, responseData) => resolve({ error: { status: xhr?.status ?? 500, data: responseData ?? xhr } })
        );
    });

export const servicesApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getServices: builder.query<Service[], ServiceListParams>({
            query: ({ clientId, isArchived }) => ({
                resource: 'crm-services',
                action: 'index',
                method: 'GET',
                useAll: true, // Используем .all() для получения списка
                queryBuilder: (query: LaravelQueryBuilder) => {
                    // Сначала применяем фильтры, потом связи
                    if (isArchived !== null && isArchived !== undefined) {
                        query.where('is_archived', isArchived);
                    }
                    
                    if (clientId) {
                        query.whereHas('clients', (clientsQuery: LaravelQueryBuilder) =>
                            clientsQuery.where('client_id', '=', clientId)
                        );
                        query.with('clients', (clientsQuery: LaravelQueryBuilder) =>
                            clientsQuery.orderBy('created_at').where('client_id', '=', clientId)
                        );
                    } else {
                        // Если clientId нет, все равно загружаем связи clients
                        query.with('clients');
                    }
                    
                    return query;
                },
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Service, id: 'LIST' },
                          ...result.map((item) => ({ type: apiTags.Service, id: item.id })),
                      ]
                    : [{ type: apiTags.Service, id: 'LIST' }],
        }),
        updateServicePaid: builder.mutation<Service, UpdateServicePaidParams>({
            query: ({ ids, isPaid }) => ({
                resource: 'crm-services',
                action: 'paid',
                method: 'PUT',
                id: ids[0],
                data: {
                    is_paid: isPaid ? 1 : 0,
                    ...(ids.length > 1 ? { ids: ids } : {}),
                },
            }),
            invalidatesTags: [{ type: apiTags.Service, id: 'LIST' }],
        }),
        assignServices: builder.mutation<unknown, AssignServicesParams>({
            query: ({ valuationsList, apiParams }) => ({
                resource: 'crm-services',
                action: 'assign',
                method: 'POST',
                data: valuationsList
                    ? { ...apiParams, valuations: valuationsList }
                    : apiParams,
            }),
            invalidatesTags: [{ type: apiTags.Service, id: 'LIST' }],
        }),
        getAllServices: builder.query<Service[], { isArchived?: boolean | null }>({
            query: ({ isArchived }) => ({
                resource: 'crm-services',
                action: 'index',
                method: 'GET',
                useAll: true,
                queryBuilder: (query: LaravelQueryBuilder) => {
                    query.with('clients', (clientsQuery: LaravelQueryBuilder) =>
                        clientsQuery
                            .with('client', (clientQuery: LaravelQueryBuilder) =>
                                clientQuery.with('mainContact')
                            )
                            .with('payments', (paymentsQuery: LaravelQueryBuilder) =>
                                paymentsQuery.with('tariff')
                            )
                    );
                    if (isArchived !== null && isArchived !== undefined) {
                        query.where('is_archived', isArchived);
                    }
                    return query;
                },
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Service, id: 'LIST' },
                          ...result.map((item) => ({ type: apiTags.Service, id: item.id })),
                      ]
                    : [{ type: apiTags.Service, id: 'LIST' }],
        }),
        getAllServicesCompressed: builder.query<unknown, { userId: number | string; isArchived?: boolean | null }>({
            query: ({ userId, isArchived }) => ({
                resource: 'crm-services',
                action: 'table-report',
                method: 'GET',
                params: {
                    is_archived: isArchived,
                    responsible_id: userId,
                },
            }),
            providesTags: [{ type: apiTags.Service, id: 'COMPRESSED' }],
        }),
        createService: builder.mutation<ApiResponse<Service>, Record<string, any>>({
            queryFn: (data) => callServiceRequest<Service>(Api.post('crm-services', 'store', data)),
            invalidatesTags: [{ type: apiTags.Service, id: 'LIST' }],
        }),
        updateService: builder.mutation<ApiResponse<Service>, { id: number; data: Record<string, any> }>({
            queryFn: ({ id, data }) => callServiceRequest<Service>(Api.putArg('crm-services', 'update', id, data)),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Service, id: arg.id },
                { type: apiTags.Service, id: 'LIST' },
            ],
        }),
        updateServiceArchive: builder.mutation<Service, UpdateServiceArchiveParams>({
            query: ({ id, isArchived }) => ({
                resource: 'crm-services',
                action: 'update',
                method: 'PUT',
                id: id,
                data: { is_archived: isArchived },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Service, id: arg.id },
                { type: apiTags.Service, id: 'LIST' },
            ],
        }),
        calculateSuccessFee: builder.mutation<unknown, CalculateSuccessFeeParams>({
            query: ({ clientId }) => ({
                resource: 'crm-services',
                action: 'calculate-success',
                method: 'POST',
                data: { client_id: clientId },
            }),
            invalidatesTags: [{ type: apiTags.Service, id: 'LIST' }],
        }),
        generateServiceExcel: builder.mutation<unknown, GenerateServiceExcelParams>({
            query: ({ data, client, service }) => ({
                resource: 'crm-services',
                action: 'generate-excel',
                method: 'POST',
                data: { data, client, service },
            }),
            invalidatesTags: [{ type: apiTags.Service, id: 'LIST' }],
        }),
        getServicesOnConfirmation: builder.query<Service[], ServicesOnConfirmationParams>({
            query: ({ clientId }) => ({
                resource: 'crm-services',
                action: 'index',
                method: 'GET',
                queryBuilder: (query: LaravelQueryBuilder) => {
                    return query
                        .with('clients', (clientsQuery: LaravelQueryBuilder) =>
                            clientsQuery.orderBy('created_at').where('client_id', '=', clientId)
                        )
                        .whereHas('clients', (clientsQuery: LaravelQueryBuilder) => {
                            return clientsQuery.where('client_id', '=', clientId);
                        })
                        .where('is_archived', '=', false);
                },
            }),
            providesTags: (result, error, arg) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Service, id: `CONFIRMATION_${arg.clientId}` },
                          ...result.map((item) => ({ type: apiTags.Service, id: item.id })),
                      ]
                    : [{ type: apiTags.Service, id: `CONFIRMATION_${arg.clientId}` }],
        }),
    }),
});

export const {
    useGetServicesQuery,
    useUpdateServicePaidMutation,
    useAssignServicesMutation,
    useGetAllServicesQuery,
    useGetAllServicesCompressedQuery,
    useCreateServiceMutation,
    useUpdateServiceMutation,
    useUpdateServiceArchiveMutation,
    useCalculateSuccessFeeMutation,
    useGenerateServiceExcelMutation,
    useGetServicesOnConfirmationQuery,
} = servicesApi;

